import { Injectable } from '@angular/core';

export interface ChatbotNotificationSettings {
  enabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AudioNotificationService {
  private readonly STORAGE_KEY = 'chatbot_notification_settings';
  private readonly SOUND_PATH = 'assets/sounds/cbot.mp3';
  private readonly MAX_QUEUE_SIZE = 5; // Limit queue to prevent excessive notifications
  private currentSound: HTMLAudioElement | null = null;
  private isPlaying: boolean = false;
  private notificationQueue: Array<() => void> = []; // Queue for pending notifications

  private defaultSettings: ChatbotNotificationSettings = {
    enabled: true,
  };

  constructor() {
    this.initializeSettings();
  }

  /**
   * Initialize settings from localStorage or use defaults
   */
  private initializeSettings() {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) {
      this.saveSettings(this.defaultSettings);
    }
  }

  /**
   * Get current notification settings
   */
  getSettings(): ChatbotNotificationSettings {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return { ...this.defaultSettings, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
    }
    return { ...this.defaultSettings };
  }

  /**
   * Save notification settings
   */
  saveSettings(settings: ChatbotNotificationSettings) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving notification settings:', error);
    }
  }

  /**
   * Update specific setting
   */
  updateSetting<K extends keyof ChatbotNotificationSettings>(
    key: K,
    value: ChatbotNotificationSettings[K]
  ) {
    const settings = this.getSettings();
    settings[key] = value;
    this.saveSettings(settings);
  }

  /**
   * Play notification sound with queueing support
   * Queues multiple notifications and plays them sequentially
   *
   * Test Cases:
   * - TC-001: Sound plays when message arrives AND user NOT in window
   * - TC-002: Sound does NOT play when user IS in window
   * - TC-003: Multiple messages queue and play sequentially
   */
  playNotification(options?: {
    isUserActive?: boolean;
    isSystemMessage?: boolean;
  }) {
    const { isUserActive = false, isSystemMessage = false } = options || {};

    // Don't play for system messages
    if (isSystemMessage) {
      return;
    }

    const settings = this.getSettings();

    // Notifications disabled
    if (!settings.enabled) {
      return;
    }

    // User is actively viewing - do not play
    if (isUserActive) {
      return;
    }

    // Add to queue if not at capacity
    if (this.notificationQueue.length >= this.MAX_QUEUE_SIZE) {
      console.warn('Notification queue full, skipping notification');
      return;
    }

    // Queue the notification
    this.notificationQueue.push(() => this.playSound());

    // Process queue if not already playing
    if (!this.isPlaying) {
      this.processQueue();
    }
  }

  /**
   * Process queued notifications sequentially
   */
  private processQueue() {
    if (this.notificationQueue.length === 0 || this.isPlaying) {
      return;
    }

    const playNextSound = this.notificationQueue.shift();
    if (playNextSound) {
      try {
        playNextSound();
      } catch (error) {
        console.error('Audio notification playback failed:', error);
        this.isPlaying = false;
        // Continue with next in queue
        this.processQueue();
      }
    }
  }

  /**
   * Play the notification sound using native Audio API
   */
  private playSound() {
    // Stop any currently playing sound
    this.stopCurrentSound();

    this.currentSound = new Audio(this.SOUND_PATH);
    this.currentSound.volume = 1;

    // Mark as playing
    this.isPlaying = true;

    // Reset flag when sound ends and process next in queue
    this.currentSound.onended = () => {
      this.isPlaying = false;
      this.processQueue(); // Play next queued notification
    };

    this.currentSound.onerror = () => {
      this.isPlaying = false;
      this.processQueue(); // Continue with next even if this one failed
    };

    this.currentSound
      .play()
      .then(() => {
        // Sound started playing successfully
      })
      .catch(error => {
        console.warn('Notification sound playback failed:', error);
        console.warn('This may be due to browser autoplay restrictions');
        this.isPlaying = false;
        this.processQueue(); // Continue with next even if playback failed
      });
  }

  /**
   * Stop any currently playing notification and clear queue
   */
  stopNotification() {
    this.notificationQueue = []; // Clear queue
    this.stopCurrentSound();
  }

  /**
   * Stop and clean up the current sound instance
   */
  private stopCurrentSound() {
    if (this.currentSound) {
      this.currentSound.pause();
      this.currentSound.currentTime = 0;
      this.currentSound.onended = null;
      this.currentSound.onerror = null;
      this.currentSound = null;
    }
    this.isPlaying = false;
  }

  /**
   * Enable/disable notifications
   */
  setEnabled(enabled: boolean) {
    this.updateSetting('enabled', enabled);
  }

  /**
   * Reset settings to defaults
   */
  resetToDefaults() {
    this.saveSettings({ ...this.defaultSettings });
  }
}
